import { Box, TextField, Typography } from "@mui/material"
import { Field, Form, Formik, FormikHelpers } from "formik"
import * as yup from "yup"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { BackButton } from "../../../shared/components/buttons/BackButton"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { resetPassword } from "../loginApi"
import { StandardButton } from "../../../shared/components/buttons/StandardButton"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { useLoginFlow } from "../../../shared/hooks/useLoginFlow"
import { useAppDispatch } from "../../store/storeHooks"
import { setUsername } from "../loginSlice"
import { makeStyles } from "tss-react/mui"
import { StandardLoadingButton } from "../../../shared/components/buttons/LoadingButton"

type FormValues = {
    email: string
}

type ForgotPasswordProps = Readonly<{
    isDefault?: boolean
    isActivateDa?: boolean
}>

const useStyles = makeStyles()((theme) => ({
    formContainer: {
        maxWidth: "25rem",
        width: "100%",
        margin: "auto",
        [theme.breakpoints.down(800)]: {
            flexDirection: "column",
            margin: "0 auto",
        },
    },
    welcomeText: {
        textAlign: "left",
        marginBottom: "0.5rem",
        fontWeight: 400,
        fontSize: "2.5rem",
    },
    welcomeSubText: {
        textAlign: "left",
        marginBottom: "1rem",
        color: theme.palette.text.secondary,
    },
    submitButton: {
        marginTop: "1rem",
        padding: "0.75rem",
        backgroundColor: "#000",
        color: "#fff",
        borderRadius: "3rem",
        "&:hover": {
            backgroundColor: "#333",
        },
    },
    buttonsContainer: {
        marginTop: "1.5rem",
    },
}))

export const ForgotPassword = ({ isDefault = true, isActivateDa = false }: ForgotPasswordProps) => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dispatch = useAppDispatch()
    const { handleAuthResponse } = useLoginFlow(isDefault, isActivateDa)

    const onSubmit = async ({ email }: FormValues, _helpers: FormikHelpers<FormValues>) => {
        const response = await callResetPassword({ email })

        dispatch(setUsername({ username: email, rememberMe: false }))
        handleAuthResponse(response)
    }

    const initialValues = {
        email: "",
    }

    const validationSchema = yup.object({
        // for valid response use
        // test@gopay.menu
        email: yup.string().required(l10n.getString("forgot-password-username-required")),
    })

    const { mutateAsync: callResetPassword } = usePublicApiMutation(resetPassword, {})

    return (
        <Box className={classes.formContainer}>
            <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ errors, touched, handleSubmit, isValid, isSubmitting }) => (
                    <Box component={Form} onSubmit={handleSubmit}>
                        {isDefault ? (
                            <LocalizedStrict id="forgot-password-username-title">
                                <Typography component="h1" variant="h5" align="left">
                                    Forgotten Password
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="activation-account-username-title">
                                <Typography variant="h3">Activate account</Typography>
                            </LocalizedStrict>
                        )}
                        {!isDefault && (
                            <LocalizedStrict id="activation-account-username-subtitle">
                                <Typography
                                    variant="body1"
                                    marginBottom="1rem"
                                    marginTop=".5rem"
                                    color="rgba(0, 0, 0, 0.6)"
                                >
                                    In order to use Facilitynet, please activate your account below
                                </Typography>
                            </LocalizedStrict>
                        )}
                        <LocalizedStrict id="forgot-password-enter-username-field" attrs={{ label: true }}>
                            <Field
                                as={TextField}
                                name="email"
                                label="Enter your username"
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={touched.email && !!errors.email}
                                helperText={touched.email && errors.email}
                                autoComplete="email"
                            />
                        </LocalizedStrict>

                        <Box className={classes.buttonsContainer}>
                            <LocalizedStrict
                                id={isDefault ? "forgot-password-button-label" : "activation-account-username-button"}
                            >
                                <StandardLoadingButton
                                    loading={isSubmitting}
                                    type="submit"
                                    fullWidth={isDefault}
                                    variant="contained"
                                    sx={{ marginTop: 3, marginBottom: 2, minWidth: isDefault ? undefined : "240px" }}
                                    disabled={!isValid}
                                >
                                    {isDefault ? "Next" : "Activate"}
                                </StandardLoadingButton>
                            </LocalizedStrict>
                            {isDefault && <BackButton />}
                        </Box>
                    </Box>
                )}
            </Formik>
        </Box>
    )
}
